import { useQueries } from '@tanstack/react-query';
import { useGetProductCategoryQuery, useSearchCollectionImagesQuery } from '@vizit/api';
import type { APIError, CollectionView } from '@vizit/api';
import { useShowActionsBar } from '@vizit/feature-flags';
import { CollectionCardGrid, type CollectionItem } from '@vizit/organisms';
import { gridClasses } from '@vizit/organisms';
import { cx } from '@vizit/theme';
import * as React from 'react';
import type { PressEvent } from 'react-aria';
import { useCollectionDownload } from '../../hooks/use-collection-download';
import { useCollectionGrid } from '../../providers';
import { useCollectionGridViewer } from './collection-grid-viewer.hooks';

interface CollectionGridViewerProps {
  /**
   * Additional CSS classes to apply to the grid container
   * @type {string}
   * @optional
   */
  className?: string;
  /**
   * Create collection dialog to display in the empty state
   * @type {React.ReactNode}
   * @optional
   */
  createCollectionDialog?: React.ReactNode;
  /**
   * Action button to display in the header (e.g., Create collection button)
   * @type {React.ReactNode}
   * @optional
   */
  headerAction?: React.ReactNode;
  /**
   * Callback when a collection is clicked
   * @type {(id: string, event: PressEvent) => void}
   * @required
   */
  onNavigateToCollection: (id: string, event: PressEvent) => void;
  /**
   * Whether to automatically calculate optimal initial load count
   * @default true
   */
  useOptimalInitialLoad?: boolean;
  /**
   * Whether collection cards show their product category badge
   * @default true
   */
  showCategory?: boolean;
  /**
   * Callback when a collection is renamed
   * @optional
   */
  onCollectionRename?: (collectionId: string, newName: string) => void;
  /**
   * Callback when a collection is deleted
   * @optional
   */
  onCollectionDelete?: (collectionId: string) => void;
  /**
   * Callback when an authentication error occurs
   * @optional
   */
  onAuthenticationError?: (error: APIError) => void;
}

/**
 * Transforms CollectionView data into CollectionItem format for display
 * @param collection - The collection data
 * @param previewImageUrl - The preview image URL fetched from the first image
 * @param previewImageFallbackUrl - Full-size fallback URL if the thumbnail fails to load
 * @param badgeBackgroundColor - Background color for the category badge
 * @param badgeBorderColor - Border color for the category badge
 */
function transformCollectionToItem(
  collection: CollectionView,
  previewImageUrl: string | null,
  previewImageFallbackUrl: string | null,
  badgeBackgroundColor?: string | null,
  badgeBorderColor?: string | null,
): CollectionItem {
  return {
    id: collection.id,
    title: collection.name,
    itemCount: collection.images?.length ?? 0,
    previewImageUrl,
    previewImageFallbackUrl,
    // Nullable in the spec since the dev1 regeneration (BLU-369): a collection can have no category
    badgeText: collection.product_category_name ?? '',
    badgeBackgroundColor,
    badgeBorderColor,
  };
}

/**
 * CollectionGridViewer component that fetches and displays portfolio collections
 *
 * @param props - The component props
 * @param props.className - Additional CSS classes
 * @param props.createCollectionDialog - Create collection dialog for empty state
 * @param props.onNavigateToCollection - Callback when a collection is clicked
 * @param props.useOptimalInitialLoad - Whether to use optimal initial load count
 * @param props.showCategory - Whether collection cards show their product category badge
 */
const CollectionGridViewer = React.memo(function CollectionGridViewer(props: CollectionGridViewerProps) {
  const {
    className,
    createCollectionDialog,
    headerAction,
    onNavigateToCollection,
    useOptimalInitialLoad,
    showCategory = true,
    onCollectionRename,
    onCollectionDelete,
    onAuthenticationError,
  } = props;

  const { workspaceId, organizationId } = useCollectionGrid();

  const { collections, totalCount, isLoading, hasNextPage, isFetchingNextPage, sentinelRef, loadingCount } =
    useCollectionGridViewer({
      useOptimalInitialLoad,
    });

  // Fetch preview images for each collection using search endpoint (oldest image first)
  // Using select to extract only the URL - React Query memoizes the selected value
  const previewImageUrls = useQueries({
    queries: collections.map((collection: CollectionView) => {
      const hasImages = (collection.images?.length ?? 0) > 0;
      return {
        queryKey: useSearchCollectionImagesQuery.getKey({
          workspace_id: workspaceId ?? '',
          collection_id: collection.id,
          sort_by: 'created_at',
          sort_direction: 'asc',
          limit: 1,
          offset: 0,
          parent_id: undefined,
          source: undefined,
          exclude_source: undefined,
          image_ids: undefined,
        }),
        queryFn: async () => {
          if (!hasImages || !workspaceId) {
            return null;
          }
          return useSearchCollectionImagesQuery.fetcher({
            workspace_id: workspaceId,
            collection_id: collection.id,
            sort_by: 'created_at',
            sort_direction: 'asc',
            limit: 1,
            offset: 0,
            parent_id: undefined,
            source: undefined,
            exclude_source: undefined,
            image_ids: undefined,
          })();
        },
        select: (data: Awaited<ReturnType<ReturnType<typeof useSearchCollectionImagesQuery.fetcher>>> | null) => ({
          thumbnail: data?.data?.[0]?.url_360 ?? null,
          full: data?.data?.[0]?.url ?? null,
        }),
        enabled: hasImages && !!workspaceId,
        staleTime: 1000 * 60 * 5, // Cache for 5 minutes
      };
    }),
    combine: (results) => results.map((r) => r.data ?? { thumbnail: null, full: null }),
  });

  // Fetch categories for each collection to get custom badge colors
  // Using select to extract only the colors - React Query memoizes the selected value
  const badgeColors = useQueries({
    queries: showCategory
      ? collections.map((collection: CollectionView) => {
          const categoryId = collection.product_category_id;
          return {
            queryKey: useGetProductCategoryQuery.getKey({
              category_id: categoryId ?? '',
            }),
            queryFn: async () => {
              if (!categoryId) {
                return null;
              }
              return useGetProductCategoryQuery.fetcher({
                category_id: categoryId,
              })();
            },
            select: (data: Awaited<ReturnType<ReturnType<typeof useGetProductCategoryQuery.fetcher>>> | null) => ({
              bg: data?.color_label_bg ?? null,
              border: data?.color_label_border ?? null,
            }),
            enabled: !!categoryId,
          };
        })
      : [],
    combine: (results) => results.map((r) => r.data ?? { bg: null, border: null }),
  });

  // Map collections to items with preview images and category colors
  const collectionItems = React.useMemo(() => {
    return collections.map((collection: CollectionView, index: number) => {
      const previewImage = previewImageUrls[index] ?? { thumbnail: null, full: null };
      const previewImageUrl = previewImage.thumbnail ?? previewImage.full;
      const previewImageFallbackUrl = previewImage.thumbnail ? previewImage.full : null;
      const badgeBackgroundColor = badgeColors[index]?.bg ?? null;
      const badgeBorderColor = badgeColors[index]?.border ?? null;

      return transformCollectionToItem(
        collection,
        previewImageUrl,
        previewImageFallbackUrl,
        badgeBackgroundColor,
        badgeBorderColor,
      );
    });
  }, [collections, previewImageUrls, badgeColors]);

  // "Download collection" in the card menu, with the Actions Bar (SDD D57, D78)
  const showActionsBar = useShowActionsBar();
  const { downloadCollection } = useCollectionDownload();
  const handleCollectionDownload = React.useCallback(
    (collectionId: string) => {
      const collection = collectionItems.find((item) => item.id === collectionId);
      if (!collection || !workspaceId) return;
      void downloadCollection({
        workspaceId,
        collectionId,
        collectionName: collection.title,
        imageCount: collection.itemCount,
      });
    },
    [collectionItems, workspaceId, downloadCollection],
  );

  const handleCollectionPress = React.useCallback(
    (id: string, event: PressEvent) => {
      onNavigateToCollection(id, event);
    },
    [onNavigateToCollection],
  );

  return (
    <>
      <CollectionCardGrid
        collections={collectionItems}
        totalCount={totalCount}
        isLoading={isLoading}
        loadingCount={loadingCount}
        showCategory={showCategory}
        className={className}
        onCollectionPress={handleCollectionPress}
        emptyStateContent={createCollectionDialog}
        headerAction={headerAction}
        workspaceId={workspaceId}
        organizationId={organizationId}
        onCollectionRename={onCollectionRename}
        onCollectionDelete={onCollectionDelete}
        onCollectionDownload={showActionsBar && workspaceId ? handleCollectionDownload : undefined}
        onAuthenticationError={onAuthenticationError}
      />

      {/* Sentinel element for infinite scroll detection */}
      {hasNextPage && <div ref={sentinelRef} className="h-4 w-full" aria-label="Loading more collections" />}

      {/* Show loading collections when fetching next page */}
      {isFetchingNextPage && loadingCount ? (
        <div className={cx(gridClasses())}>
          {/* Skeleton loading cards are handled by CollectionCardGrid internally */}
        </div>
      ) : null}
    </>
  );
});

export { CollectionGridViewer };
export type { CollectionGridViewerProps };
